/**
 * Utility functions for API calls
 */

import type { ApiResponse, ErrorResponse } from '@/lib/types/api';
import { ApiError } from './error';
import { getAccessToken } from '@/lib/auth/token-store';
import { refreshAccessToken } from '@/lib/auth/refresh-client';

interface FetchOptions extends RequestInit {
  requireAuth?: boolean;
}

let logoutCallback: (() => void) | null = null;

export const setLogoutCallback = (callback: () => void) => {
  logoutCallback = callback;
};

/**
 * Get or generate a device ID for tracking and identification
 */
function getDeviceId(): string {
  // Check if we're in a browser environment
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
    // Return a temporary ID for server-side rendering
    return 'ssr_' + Math.random().toString(36).substring(2) + '_' + Date.now();
  }

  let deviceId = localStorage.getItem('visitor_id');
  if (!deviceId) {
    deviceId =
      'visitor_' + Math.random().toString(36).substring(2) + '_' + Date.now();
    localStorage.setItem('visitor_id', deviceId);
  }
  return deviceId;
}

/**
 * Enhanced fetch function with error handling and authentication
 */
export async function fetchApi<T>(
  url: string,
  options: FetchOptions = { requireAuth: true }
): Promise<ApiResponse<T>> {
  const { requireAuth = true, ...fetchOptions } = options;

  const headers = new Headers(options.headers);

  // Add device ID header to all requests
  headers.set('X-Device-ID', getDeviceId());

  if (requireAuth) {
    // Check if we're in a browser environment
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
      throw new Error(
        'Authentication not available during server-side rendering'
      );
    }

    const token = getAccessToken();
    if (!token) {
      throw new Error('Session expired. Please login again');
    }
    headers.set('Authorization', `Bearer ${token}`);
  }

  // Only set Content-Type if body is not FormData
  if (!(options.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }

  // A FormData body is a stream that a retry would consume twice, so those
  // requests get one attempt and surface the 401 normally.
  const canRetry = requireAuth && !(options.body instanceof FormData);

  try {
    let response = await fetch(url, {
      ...fetchOptions,
      headers,
    });

    // The access token lapsed mid-session. Renew it once and replay the request
    // rather than dumping the user on the login screen mid-task.
    if (response.status === 401 && canRetry) {
      const renewedToken = await refreshAccessToken();
      if (renewedToken) {
        // Fresh Headers rather than mutating the ones the first request holds.
        const retryHeaders = new Headers(headers);
        retryHeaders.set('Authorization', `Bearer ${renewedToken}`);
        response = await fetch(url, {
          ...fetchOptions,
          headers: retryHeaders,
        });
      }
    }

    const rawBody = await response.text();
    const data: unknown = rawBody
      ? (() => {
          try {
            return JSON.parse(rawBody) as unknown;
          } catch {
            return { message: rawBody };
          }
        })()
      : null;

    // Still unauthorized after a refresh attempt — the session is genuinely over.
    if (response.status === 401) {
      // Call logout if available
      if (logoutCallback) {
        logoutCallback();
      }
      const message =
        extractApiErrorMessage(data) || 'Session expired. Please login again.';
      throw new Error(message);
    }

    if (!response.ok) {
      const details = extractApiErrorDetails(data);
      const message =
        details.message || `Request failed with status ${response.status}`;
      throw new ApiError(
        response.status,
        details.code || String(response.status),
        message,
        details.data
      );
    }

    if (!data) {
      return { result: undefined as T, status: response.status };
    }

    return data as ApiResponse<T>;
  } catch (error) {
    if (error instanceof Error) {
      // Add more context to network errors
      if (error.message.includes('fetch')) {
        throw new Error(`Network error: ${error.message}`);
      }
      throw error;
    }
    throw new Error('An unexpected error occurred');
  }
}

function extractApiErrorMessage(data: unknown): string | undefined {
  if (!data) return undefined;
  if (typeof data === 'string') return data;
  if (typeof data !== 'object') return undefined;

  const maybe = data as Record<string, unknown>;

  const errorObj = maybe?.error as Record<string, unknown> | undefined;
  const errorMessage = errorObj?.errorMessage;
  if (typeof errorMessage === 'string' && errorMessage.trim()) {
    return errorMessage;
  }

  const topLevelErrorMessage = maybe?.errorMessage;
  if (typeof topLevelErrorMessage === 'string' && topLevelErrorMessage.trim()) {
    return topLevelErrorMessage;
  }

  const message = maybe?.message;
  if (typeof message === 'string' && message.trim()) {
    return message;
  }

  const error = maybe?.error;
  if (typeof error === 'string' && error.trim()) {
    return error;
  }

  const nestedErrorMessage = errorObj?.message;
  if (typeof nestedErrorMessage === 'string' && nestedErrorMessage.trim()) {
    return nestedErrorMessage;
  }

  const errorCode = errorObj?.errorCode as Record<string, string[]> | undefined;
  if (errorCode && typeof errorCode === 'object') {
    for (const value of Object.values(errorCode)) {
      if (Array.isArray(value) && typeof value[0] === 'string' && value[0]) {
        return value[0];
      }
    }
  }

  return undefined;
}

function extractApiErrorDetails(data: unknown): {
  message?: string;
  code?: string;
  data?: unknown;
} {
  if (!data || typeof data !== 'object') {
    return { message: extractApiErrorMessage(data) };
  }

  const maybe = data as Record<string, unknown>;
  const errorObj = maybe.error as Record<string, unknown> | undefined;
  const payload = errorObj?.data;
  const payloadCode =
    payload && typeof payload === 'object'
      ? (payload as Record<string, unknown>).code
      : undefined;

  return {
    message: extractApiErrorMessage(data),
    code:
      (typeof payloadCode === 'string' && payloadCode) ||
      (typeof errorObj?.errorCode === 'string' && errorObj.errorCode) ||
      undefined,
    data: payload,
  };
}

/**
 * Check if a response is an error response
 */
export function isErrorResponse(response: unknown): response is ErrorResponse {
  return (
    typeof response === 'object' &&
    response !== null &&
    'message' in response &&
    'status' in response
  );
}
